import React, { useRef, useEffect, ReactNode, memo, useMemo } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import { usePerformance } from '../hooks/usePerformance';

interface ScrollRevealProps {
  children: ReactNode;
  delay?: number;
  threshold?: number;
  rootMargin?: string;
}

export const ScrollReveal: React.FC<ScrollRevealProps> = memo(({ 
  children, 
  delay = 0, 
  threshold = 0.1,
  rootMargin = '0px 0px -100px 0px'
}) => {
  const ref = useRef(null);
  const { markRenderStart } = usePerformance('ScrollReveal');
  
  // Use intersection observer with custom options for better performance
  const isInView = useInView(ref, { 
    once: true
  });
  
  const mainControls = useAnimation();

  // Memoize variants to prevent recreation on every render
  const variants = useMemo(() => ({
    hidden: { opacity: 0, y: 75 },
    visible: { opacity: 1, y: 0 }
  }), []);

  // Memoize transition to prevent recreation
  const transition = useMemo(() => ({ 
    duration: 0.5, 
    delay
  }), [delay]);

  useEffect(() => {
    markRenderStart();
    
    if (isInView) {
      mainControls.start('visible');
    }
  }, [isInView, mainControls, markRenderStart]);

  return (
    <motion.div
      ref={ref}
      variants={variants}
      initial="hidden"
      animate={mainControls}
      transition={transition}
    >
      {children}
    </motion.div>
  );
});
